/** @jsx jsx */
import { jsx } from '@front10/base-elements';
import React, { memo } from 'react';
import { useEffect, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import DatePicker from '../DatePicker';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import { useRef } from 'react';
import LegendCalendar from '../../others/Legend/LegendCalendar';
import { useFareItemProps } from './../../../../src/components/containers/_commons/tracking';
import {
  isPastDate,
  isCurrentDay,
  isSameDate,
  isOutsideDay
} from '../../../helpers/dayCard/dayCard';
import ErrorBoundary from '../../utils/ErrorBoundary';
import DateControl from '../../controls/DateControl';
import FareItemMediumSize from '../../../components/items/FareItemMediumSize';
import { weekDaysLong } from './../../../helpers/localization/localizationUtils';
import { getRelativeDateLocalization } from './../../../helpers/localization/localizationUtils';
import { TabList, TabPanels, TabPanel, Tabs } from '@reach/tabs';
import { Inline } from '@front10/base-elements';
import { Tab } from '@reach/tabs';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import SuggestedDate from './atoms/SuggestedDate';

const KEY_ENTER = 13;
const KEY_SPACE = 32;
const ROUND_TRIP = 'ROUND_TRIP';
const ONE_WAY = 'ONE_WAY';
const EMPTY_FROM_API = 'EMPTY_FROM_API';
const KEY_TAB = 9;
const KEY_END = 35;
const KEY_HOME = 36;
const KEY_ARROW_LEFT = 37;
const KEY_ARROW_UP = 38;
const KEY_ARROW_RIGHT = 39;
const KEY_ARROW_DOWN = 40;

function FareCalendar(props) {
  const isOriginSelected = Boolean(props.filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(props.filtersState.locations.destination.value);
  const {
    direction,
    translate,
    localizationData,
    theme: { designTokens }
  } = useContext(Context);
  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_FIRST_DAY': {
          return { ...state, firstDay: action.payload };
        }
        case 'SET_START_DAY': {
          return { ...state, start: action.payload };
        }
        case 'SET_END_DAY': {
          return { ...state, end: action.payload };
        }
        case 'SET_SELECTED_COMPONENT': {
          return { ...state, selectedComponent: action.payload };
        }
        case 'SET_SUGGEST_FARE': {
          return { ...state, suggestFare: action.payload };
        }
        case 'SET_FARE': {
          return { ...state, fare: action.payload };
        }
        case 'SET_FOCUS_DATE': {
          return { ...state, focusDate: action.payload };
        }
        case 'SET_CLICK_ACTION': {
          return { ...state, clickAction: action.payload };
        }
        case 'SET_ENTER_ACTION': {
          return { ...state, enterAction: action.payload };
        }
        case 'SET_ALLOW_BOOK_NOW_ACTION': {
          return { ...state, allowBookNowAction: action.payload };
        }
        case 'SET_DATE_TO_PRESERVE': {
          return { ...state, dateToPreserve: action.payload };
        }
        default: {
          return state;
        }
      }
    },
    {
      firstDay: dayjs().toDate(),
      start: undefined,
      end: undefined,
      selectedComponent: 'start',
      suggestFare: undefined,
      fare: undefined,
      focusDate: dayjs().toDate(),
      clickAction: undefined,
      enterAction: undefined,
      allowBookNowAction: false,
      dateToPreserve: undefined
    }
  );

  const getSuggestFare = (date) => {
    let day = state.start;
    if (!dayjs(date).isSame(state.start, 'day')) {
      day = date;
    }
    let lowFare = undefined;
    for (let index = 0; index < props.data.length; index++) {
      const item = props.data[index];
      if (item.isLowest && item.fareItem.dataType !== 'EMPTY_FROM_API') {
        if (lowFare === undefined) {
          lowFare = { ...item };
        } else {
          if (
            Math.abs(dayjs(day).diff(dayjs(item.fareItem.returnDate), 'd')) <
            Math.abs(dayjs(day).diff(dayjs(lowFare.fareItem.returnDate), 'd'))
          ) {
            lowFare = { ...item };
          }
        }
      }
    }
    return lowFare;
  };

  const resetState = () => {
    const date =
      dayjs(state.dateToPreserve).get('month') === dayjs(new Date()).get('month')
        ? new Date()
        : dayjs(state.dateToPreserve).set('date', 1).toDate();
    dispatch({ type: 'SET_START_DAY', payload: undefined });
    dispatch({ type: 'SET_END_DAY', payload: undefined });
    dispatch({ type: 'SET_SELECTED_COMPONENT', payload: 'start' });
    dispatch({ type: 'SET_SUGGEST_FARE', payload: undefined });
    dispatch({ type: 'SET_FARE', payload: undefined });
    dispatch({ type: 'SET_FOCUS_DATE', payload: date });
    dispatch({ type: 'SET_FIRST_DAY', payload: date });
    dispatch({ type: 'SET_CLICK_ACTION', payload: undefined });
    props.setActiveTab('start');
    props.setIsStartAndReturnDateSelected(false);
  };

  useEffect(() => {
    if (props.data.length !== 0) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.flightType.value]);

  useEffect(() => {
    const currentMonth =
      props.data && props.data.length !== 0 ? dayjs(props.data[0].day).get('month') : undefined;
    if (
      filtersState.flightType.value === ROUND_TRIP &&
      !props.loading &&
      props.data.length !== 0 &&
      state.selectedComponent === 'end'
    ) {
      const lowFare = getSuggestFare(state.start);
      if (lowFare) {
        dispatch({ type: 'SET_SUGGEST_FARE', payload: lowFare });
      }
    } else if (
      filtersState.flightType.value === ONE_WAY &&
      state.start === undefined &&
      currentMonth !== undefined &&
      currentMonth === dayjs(state.dateToPreserve).get('month')
    ) {
      const lowFare = getSuggestFare(state.start);
      if (lowFare) {
        dispatch({ type: 'SET_FARE', payload: lowFare });
        dispatch({ type: 'SET_START_DAY', payload: lowFare.day });
        props.globalDispatch({
          type: 'DATE_RANGE_HISTOGRAM_SET_START_DATE',
          payload: {
            dateRange: {
              ...props.filtersState.dateRange.value,
              selected: lowFare.day
            },
            histogramType: ''
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const isBlackoutDay = (day) => {
    return props.data.filter((item) => day.isSame(item.day, 'day'))[0]?.fareItem?.isBlackoutDay;
  };

  const getNextDateAfterBlackoutDay = (day) => {
    return props.data.filter(
      (item) => dayjs(item.day).isAfter(day, 'day') && !item.fareItem.isBlackoutDay
    )[0];
  };

  const getNextDate = (newDate, oldDate) => {
    const fare = getNextDateAfterBlackoutDay(newDate);
    if (fare) {
      return dayjs(fare?.day);
    }
    return dayjs(oldDate);
  };

  const setFocusDate = (date, event) => {
    const firstDay = dayjs(state.firstDay);
    const endDay = dayjs(state.firstDay).endOf('month');
    let newDate;
    switch (event.keyCode) {
      case KEY_TAB: {
        if (event.shiftKey) {
          if (
            dayjs(date).isSame(new Date(), 'day') ||
            dayjs(date).isSame(dayjs(date).startOf('month'), 'day') ||
            (state.selectedComponent === 'end' && dayjs(date).isSame(firstDay, 'day'))
          ) {
            return;
          }
          newDate = dayjs(state.focusDate).subtract(1, 'day');
          if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
            newDate = getNextDate(newDate, state.focusDate);
          }
        } else {
          if (dayjs(date).isSame(dayjs(date).endOf('month'), 'day')) {
            return;
          }
          newDate = dayjs(state.focusDate).add(1, 'day');
          if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
            newDate = getNextDate(newDate, state.focusDate);
          }
        }
        break;
      }
      case KEY_ARROW_RIGHT: {
        newDate = dayjs(state.focusDate).add(1, 'day');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      case KEY_ARROW_LEFT: {
        newDate = dayjs(state.focusDate).subtract(1, 'day');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      case KEY_ARROW_UP: {
        newDate = dayjs(state.focusDate).subtract(7, 'day');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      case KEY_ARROW_DOWN: {
        newDate = dayjs(state.focusDate).add(7, 'day');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      case KEY_HOME: {
        event.preventDefault();
        newDate = dayjs(date).startOf('week');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      case KEY_END: {
        event.preventDefault();
        newDate = dayjs(date).endOf('week');
        if (newDate.isBefore(endDay) && newDate.isAfter(firstDay) && isBlackoutDay(newDate)) {
          newDate = getNextDate(newDate, state.focusDate);
        }
        break;
      }
      default: {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
    if (
      newDate &&
      (firstDay.isSame(newDate.toDate(), 'day') ||
        endDay.isSame(newDate.toDate(), 'day') ||
        (firstDay.isBefore(newDate.toDate(), 'day') && endDay.isAfter(newDate.toDate(), 'day')))
    ) {
      dispatch({ type: 'SET_FOCUS_DATE', payload: newDate.toDate() });
      dispatch({ type: 'SET_CLICK_ACTION', payload: false });
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const toMonth = dayjs()
    .add(props.numberOfMonths - 1, 'month')
    .toDate();

  useEffect(() => {
    let firstDay = dayjs();
    dispatch({ type: 'SET_START_DAY', payload: undefined });
    dispatch({ type: 'SET_CLICK_ACTION', payload: false });
    dispatch({ type: 'SET_FOCUS_DATE', payload: firstDay.toDate() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daysLabels = weekDaysLong(translate, localizationData);
  const getDayInformation = (day) => {
    const dayName = day ? daysLabels[day?.getDay()] : '';
    const isCurrentDate = dayjs(new Date()).isSame(day, 'day');
    const prefixArialLabel = translate('cmp-low-fare-calendar-oi-date-available', `Choose`);
    return `${prefixArialLabel} ${
      isCurrentDate ? translate('cmp-legend-current-day', 'Current day') : ''
    } ${dayName}`;
  };

  const getInformationByFare = (fare) => {
    if (fare?.dataType === undefined || fare?.dataType === 'EMPTY_FROM_API') {
      return `${translate(
        'cmp-low-fare-calendar-oi-no-available-result',
        'No available results'
      )} ${translate('cmp-low-fare-calendar-oi-check-availability-info', 'Check Availability')}`;
    } else if (fare?.dataType === 'DATA_API') {
      const lastView = `${dayjs()
        .locale(getRelativeDateLocalization(translate, localizationData), null, true)
        .to(
          props?.dayCard?.dayCardLastSeen?.searchDate,
          props?.dayCard?.dayCardLastSeen?.withoutSuffix
        )}${
        props?.dayCard?.dayCardLastSeen?.withoutSuffix
          ? translate('cmp-low-fare-calendar-oi-ago-label', 'ago')
          : ''
      }`;
      return `${translate('cmp-low-fare-calendar-oi-from-label', 'From')} ${fare?.currencyCode} ${
        fare?.formattedTotalPrice
      } ${translate('cmp-price-disclaimer', 'Price subject to changes')} ${translate(
        'cmp-low-fare-calendar-oi-viewed-label',
        'Viewed'
      )} ${lastView} ${translate(
        `cmp-travel-class--${fare?.farenetTravelClass.toLowerCase().replace('_', '-')}`,
        `cmp-travel-class--${fare?.farenetTravelClass.toLowerCase().replace('_', '-')}`
      )} ${translate(
        `cmp-journey-type-options-${fare?.journeyType.toLowerCase()}`,
        `cmp-journey-type-options-${fare?.journeyType.toLowerCase()}`
      )}`;
    }
  };

  const getNewInformation = (info) => {
    const isCurrentDate = dayjs(new Date()).isSame(info.day, 'day');
    return `${
      isCurrentDate
        ? translate(
            'cmp-low-fare-calendar-oi-container-explain',
            'This is a calendar with the cheapest prices per day. Press the LEFT and RIGHT arrow keys to navigate the row by weekday. Press the UP and DOWN arrow keys to navigate between weeks on the same weekday, Press the ENTER key to activate the selected date'
          )
        : ''
    } ${getDayInformation(info.day)} ${dayjs(info.day).format(
      props?.dayCard?.formatAriaLabel ?? 'MMMM DD'
    )} ${
      info.isLowest === true ? translate('cmp-fare-finder-calendar-lowest', 'Lowest fare') : ''
    } ${getInformationByFare(info)}`;
  };

  const getInformationByData = () => {
    let information = [];
    let newInfo = '';
    props.data.forEach((element) => {
      const info = { ...element?.fareItem, isLowest: element?.isLowest, day: element?.day };
      newInfo = getNewInformation(info);
      information.push({ day: element?.day, data: newInfo });
    });
    return information;
  };

  useEffect(() => {
    if (props.isDesktop) {
      const elements = document?.querySelectorAll('.DayPicker-Day');
      const information = getInformationByData();
      let tmp;
      let date;
      information.forEach((info) => {
        elements.forEach((element) => {
          if (element !== null && element !== undefined) {
            tmp = element.querySelector('.fr-day-card-container');
            if (tmp !== undefined && tmp !== null) {
              tmp = tmp.getAttribute('id');
              date = tmp.substring(12, tmp.length);
              if (date === dayjs(info.day).format('MM-DD-YYYY')) {
                element.setAttribute('aria-label', info.data);
              }
            }
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const departureDateControlRef = useRef(null);
  const returnDateControlRef = useRef(null);
  const bookNowControlRef = useRef(null);

  useEffect(() => {
    if (
      props.filtersState.locations.origin.value === undefined ||
      props.filtersState.locations.origin.value === '' ||
      props.filtersState.locations.destination.value === undefined ||
      props.filtersState.locations.destination.value === ''
    ) {
      dispatch({ type: 'SET_START_DAY', payload: undefined });
      dispatch({ type: 'SET_END_DAY', payload: undefined });
      dispatch({ type: 'SET_FOCUS_DATE', payload: undefined });
      dispatch({ type: 'SET_FARE', payload: undefined });
    }
    if (
      props.filtersState.locations.origin.value !== undefined &&
      props.filtersState.locations.origin.value !== '' &&
      props.filtersState.locations.destination.value !== undefined &&
      props.filtersState.locations.destination.value !== ''
    ) {
      dispatch({ type: 'SET_CLICK_ACTION', payload: true });
    }
  }, [props.filtersState.locations.origin.value, props.filtersState.locations.destination.value]);

  useEffect(() => {
    dispatch({ type: 'SET_START_DAY', payload: undefined });
    dispatch({ type: 'SET_FIRST_DAY', payload: dayjs().toDate() });
    dispatch({ type: 'SET_END_DAY', payload: undefined });
    dispatch({ type: 'SET_FOCUS_DATE', payload: undefined });
    dispatch({ type: 'SET_FARE', payload: undefined });
    dispatch({ type: 'SET_SELECTED_COMPONENT', payload: 'start' });
    if (
      props.filtersState.flightType.value === ONE_WAY &&
      props.data &&
      props.data.length !== 0 &&
      dayjs(props.data[0].day).get('month') === dayjs().get('month')
    ) {
      const lowFare = getSuggestFare(state.start);
      if (lowFare) {
        dispatch({ type: 'SET_FARE', payload: lowFare });
        dispatch({ type: 'SET_START_DAY', payload: lowFare.day });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.locations.destination.value]);

  const onSelectNavMonth = (item) => {
    const { onSelectMonth, initialMonth } = props;
    const date = dayjs().set('month', item.month).set('year', item.year);
    dispatch({ type: 'SET_DATE_TO_PRESERVE', payload: date.toDate() });
    const start =
      dayjs(date).diff(dayjs(initialMonth).startOf('month'), 'month') === 0
        ? initialMonth
        : date.startOf('month').toDate();
    onSelectMonth({
      dateRange: {
        start: state.start,
        end: state.end
      },
      start,
      end: date.endOf('month').toDate(),
      selected: state.start
    });
    if (isOriginSelected && isDestinationSelected) {
      if (state.selectedComponent === 'start') {
        if (
          state.start !== undefined &&
          dayjs(state.start).get('month') === dayjs(start).get('month')
        ) {
          dispatch({ type: 'SET_FIRST_DAY', payload: state.start });
          dispatch({ type: 'SET_FOCUS_DATE', payload: state.start });
        } else {
          dispatch({ type: 'SET_FOCUS_DATE', payload: start });
          dispatch({ type: 'SET_FIRST_DAY', payload: start });
        }
        dispatch({ type: 'SET_CLICK_ACTION', payload: true });
      } else {
        if (
          state.end !== undefined &&
          dayjs(state.end).get('month') === dayjs(start).get('month')
        ) {
          dispatch({ type: 'SET_FOCUS_DATE', payload: state.end });
          if (dayjs(state.start).get('month') === dayjs(state.end).get('month')) {
            dispatch({ type: 'SET_FIRST_DAY', payload: state.start });
          } else {
            dispatch({ type: 'SET_FIRST_DAY', payload: dayjs(state.end).startOf('month') });
          }
        } else {
          if (dayjs(state.start).get('month') === dayjs(start).get('month')) {
            dispatch({ type: 'SET_FIRST_DAY', payload: state.start });
            dispatch({ type: 'SET_FOCUS_DATE', payload: state.start });
          } else {
            dispatch({ type: 'SET_FOCUS_DATE', payload: start });
            dispatch({ type: 'SET_FIRST_DAY', payload: start });
          }
        }
        dispatch({ type: 'SET_CLICK_ACTION', payload: true });
      }
    }
  };

  const getSelectedDate = () => {
    if (state.selectedComponent === 'start') {
      return state.start ? state.start : undefined;
    } else {
      return state.end ? state.end : undefined;
    }
  };

  const itemSettings = useFareItemProps(state.fare, props.globalDispatch, null);
  const settingsFareItemMediumSize = (fare) => {
    let settings = {};
    if (!fare && state.start === undefined) {
      settings = {
        focusable: props.isDesktop ? false : true,
        fareItemPrice: {
          ...props.fareItemAtomSettings.fareItemPrice,
          showPriceDisclaimer: false,
          showCurrencyCode: false
        },
        fareItemCallToAction: {
          ...props.fareItemAtomSettings.fareItemCallToAction,
          tabIndex: false,
          callToAction: {
            ...props.fareItemAtomSettings.fareItemCallToAction.callToAction,
            disabled: false
          }
        },
        showLastSeen: false,
        data: { formattedTotalPrice: translate('cmp-low-fare-calendar-oi-separator', '--') }
      };
    } else if (!isOriginSelected || !isDestinationSelected || !fare) {
      settings = {
        focusable: props.isDesktop ? false : true,
        fareItemPrice: {
          ...props.fareItemAtomSettings.fareItemPrice,
          showCurrencyCode: false,
          showPriceDisclaimer: false
        },
        fareItemCallToAction: {
          ...props.fareItemAtomSettings.fareItemCallToAction,
          callToAction: {
            ...props.fareItemAtomSettings.fareItemCallToAction.callToAction,
            disabled: !state.allowBookNowAction
          }
        },
        showLastSeen: false,
        data: !fare
          ? { formattedTotalPrice: translate('cmp-low-fare-calendar-oi-separator', '--') }
          : {
              ...fare.fareItem,
              formattedTotalPrice: translate('cmp-low-fare-calendar-oi-separator', '--')
            }
      };
    } else if (fare && fare.fareItem && fare.fareItem.dataType === EMPTY_FROM_API) {
      settings = {
        focusable: props.isDesktop ? false : true,
        fareItemPrice: {
          ...props.fareItemAtomSettings.fareItemPrice,
          marginTop: 'auto',
          showCurrencyCode: false,
          showPriceDisclaimer: false
        },
        showFareDisclaimer: false,
        availableAtomsBody: ['FareItemPrice', 'FareItemDisclaimer', 'FareItemCallToAction'],
        showLastSeen: false,
        data: {
          ...fare.fareItem,
          formattedTotalPrice: translate('cmp-low-fare-calendar-oi-separator', '--')
        },
        fareItemCallToAction: {
          ...props.fareItemAtomSettings.fareItemCallToAction,
          focusReferences: (e) => {
            bookNowControlRef.current = e;
          },
          labelKey: 'fare_call_to_action_alternative_label',
          labelKeyMin: 'fare_call_to_action_alternative_label_min'
        }
      };
    } else if (fare && fare.fareItem) {
      settings = {
        focusable: props.isDesktop ? false : true,
        data: { ...fare.fareItem },
        fareItemPrice: {
          ...props.fareItemAtomSettings.fareItemPrice,
          marginTop: 'auto',
          showCurrencyCode: true,
          showPriceDisclaimer: true
        }
      };
    }
    if (
      (filtersState.flightType.value === ROUND_TRIP &&
        (state.start === undefined || state.end === undefined)) ||
      (filtersState.flightType.value === ONE_WAY && state.start === undefined)
    ) {
      settings = {
        ...settings,
        fareItemCallToAction: {
          ...settings?.fareItemCallToAction,
          callToAction: {
            ...settings?.fareItemCallToAction?.callToAction,
            disabled: true
          }
        }
      };
    }
    return settings;
  };

  useEffect(() => {
    if (state.enterAction === 'start') {
      if (filtersState.flightType.value === ROUND_TRIP) {
        returnTabRef?.current?.focus();
      } else {
        bookNowControlRef?.current?.focus();
      }
      dispatch({ type: 'SET_ENTER_ACTION', payload: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.start]);

  useEffect(() => {
    if (state.enterAction === 'end') {
      //returnDateControlRef.current.focus();
      bookNowControlRef.current?.focus();
      dispatch({ type: 'SET_ENTER_ACTION', payload: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.end]);

  const renderDay = (day, isFirstDay, isLastDay) => {
    const {
      dayCard,
      data,
      loading,
      DayComponentInstance,
      isDesktop,
      usedErrorBoundary,
      currentMonth,
      disregardedBehavior
    } = props;
    const { firstDay } = state;

    const handlerDayClick = (date, event) => {
      if (
        date !== null &&
        (dayjs(date).isAfter(firstDay, 'day') || dayjs(date).isSame(firstDay, 'day')) &&
        (dayjs(date).isBefore(dayjs(firstDay).endOf('month').toDate(), 'day') ||
          dayjs(date).isSame(dayjs(firstDay).endOf('month').toDate(), 'day'))
      ) {
        if (filtersState.flightType.value === ROUND_TRIP) {
          let newfare = props.data.filter(
            (item) => dayjs(date).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
          )[0];
          if (state.selectedComponent === 'start') {
            if (state.start !== undefined && state.end !== undefined) {
              dispatch({ type: 'SET_END_DAY', payload: undefined });
            }
            dispatch({ type: 'SET_START_DAY', payload: date });
            props.setIsStartAndReturnDateSelected(false);
            dispatch({ type: 'SET_FARE', payload: undefined });
            const tmp =
              newfare && newfare.fareItem && newfare.fareItem.dataType === EMPTY_FROM_API
                ? false
                : true;
            dispatch({ type: 'SET_ALLOW_BOOK_NOW_ACTION', payload: tmp });
            props.globalDispatch({
              type: 'DATE_RANGE_HISTOGRAM_CHANGE_DEPARTURE',
              payload: {
                dateRange: {
                  ...props.filtersState.dateRange,
                  start: date
                },
                histogramType: ''
              }
            });
          } else if (state.selectedComponent === 'end') {
            // dispatch({ type: 'SET_ALLOW_BOOK_NOW_ACTION', payload: state.allowBookNowAction });
            if (newfare !== undefined && newfare.fareItem.dataType === EMPTY_FROM_API) {
              newfare.fareItem.returnDate = dayjs(date).format('YYYY-MM-DD');
              newfare.fareItem.departureDate = dayjs(state.start).format('YYYY-MM-DD');
            } else if (newfare === undefined) {
              newfare = {
                day: date,
                isLowest: false,
                fareItem: {
                  dataType: EMPTY_FROM_API,
                  departureDate: dayjs(state.start).format('YYYY-MM-DD'),
                  isBlackoutDay: false,
                  returnDate: dayjs(date).format('YYYY-MM-DD')
                }
              };
            }
            dispatch({ type: 'SET_END_DAY', payload: date });
            dispatch({ type: 'SET_FARE', payload: newfare });
            if (
              !props.isDesktop &&
              state.start !== undefined &&
              state.end !== undefined &&
              state.start !== null &&
              state.end !== null
            ) {
              summarySectionRef?.current?.focus();
            }
            props.globalDispatch({
              type: 'DATE_RANGE_HISTOGRAM_CHANGE_RETURN',
              payload: {
                dateRange: {
                  ...props.filtersState.dateRange,
                  end: date,
                  start: state.start
                },
                histogramType: ''
              }
            });
            props.setIsStartAndReturnDateSelected(true);
          }
          dispatch({ type: 'SET_FOCUS_DATE', payload: date });
          if (event && (event.keyCode === KEY_ENTER || event.keyCode === KEY_SPACE)) {
            if (state.selectedComponent === 'start') {
              dispatch({ type: 'SET_ENTER_ACTION', payload: 'start' });
            } else {
              dispatch({ type: 'SET_ENTER_ACTION', payload: 'end' });
            }
          }
        } else if (filtersState.flightType.value === ONE_WAY) {
          dispatch({ type: 'SET_START_DAY', payload: date });
          dispatch({ type: 'SET_CLICK_ACTION', payload: true });
          dispatch({ type: 'SET_FOCUS_DATE', payload: date });
          const newfare = props.data.filter(
            (item) => dayjs(date).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
          )[0];
          dispatch({ type: 'SET_ALLOW_BOOK_NOW_ACTION', payload: true });
          dispatch({ type: 'SET_FARE', payload: newfare });
          if (event && (event.keyCode === KEY_ENTER || event.keyCode === KEY_SPACE)) {
            dispatch({ type: 'SET_ENTER_ACTION', payload: 'start' });
          }
          if (!props.isDesktop && state.start !== undefined && state.start !== null) {
            summarySectionRef?.current?.focus();
          }
          props.globalDispatch({
            type: 'DATE_RANGE_HISTOGRAM_CHANGE_DEPARTURE',
            payload: {
              dateRange: {
                ...props.filtersState.dateRange,
                start: date
              },
              histogramType: ''
            }
          });
        }
        //change tab
        if (state.selectedComponent === 'start') {
          if (filtersState.flightType.value !== ONE_WAY) {
            dispatch({ type: 'SET_SUGGEST_FARE', payload: undefined });
            props.globalDispatch({
              type: 'DATE_RANGE_HISTOGRAM_CHANGE',
              payload: {
                dateRange: {
                  start: date,
                  end: dayjs(date).endOf('month').toDate(),
                  selected: date
                },
                histogramType: 'INBOUND'
              }
            });
            dispatch({ type: 'SET_SELECTED_COMPONENT', payload: 'end' });
            props.setActiveTab('end');
            dispatch({ type: 'SET_FOCUS_DATE', payload: date });
            dispatch({ type: 'SET_FIRST_DAY', payload: date });
            returnTabRef?.current?.setAttribute('tabindex', 0);
            setTimeout(() => {
              returnTabRef?.current?.focus();
            }, 250);
          }
        }
      }
    };

    let fare = data.filter(
      (item) => dayjs(day).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
    )[0];
    const isLowest = fare ? fare.isLowest : null;
    const isToday = isCurrentDay(day);
    const isOutside = isOutsideDay(day, currentMonth);
    fare = fare ? fare.fareItem : null;
    const style = Object.assign({}, dayCard.style);
    if (isFirstDay) {
      if (direction === 'ltr') {
        style.marginLeft = '0px';
      } else {
        style.marginRight = '0px';
      }
    }
    if (isLastDay) {
      if (direction === 'ltr') {
        style.marginRight = '0px';
      } else {
        style.marginLeft = '0px';
      }
    }

    const isDisregarded =
      dayjs(dayjs(day).format('MM/DD/YYYY')).isBefore(
        dayjs(dayjs(currentMonth).format('MM/DD/YYYY'))
      ) &&
      (dayjs(dayjs(day).format('MM/DD/YYYY')).isAfter(dayjs(dayjs().format('MM/DD/YYYY'))) ||
        dayjs(dayjs(day).format('MM/DD/YYYY')).isSame(dayjs(dayjs().format('MM/DD/YYYY'))));

    const isBlackoutDate = fare !== null ? fare.isBlackoutDay : false;
    const dayRender = (
      <Box
        className={isDesktop ? `fr-day-card-container` : ''}
        id={`fr-day-card-${dayjs(day).format('MM-DD-YYYY')}`}
        ariaHidden={true}
      >
        <DayComponentInstance
          {...dayCard}
          day={day}
          isBlackoutDay={isBlackoutDate}
          isOutside={isOutside}
          isLowest={isLowest}
          isToday={isToday}
          isDisregarded={isDisregarded}
          isEmptyCard={isEmptyCalendar}
          isFirstDateFocusable={isSameDate(day, new Date())}
          ariaLabel={
            isSameDate(day, new Date())
              ? translate(
                  'cmp-low-fare-calendar-oi-container-explain',
                  'This is a calendar with the cheapest prices per day. Press the LEFT and RIGHT arrow keys to navigate the row by weekday. Press the UP and DOWN arrow keys to navigate between weeks on the same weekday, Press the ENTER key to activate the selected date'
                )
              : null
          }
          data={fare}
          loading={loading}
          style={style}
          hover={
            isPastDate(day) ||
            (isDisregarded && disregardedBehavior === 'past-date') ||
            isEmptyCalendar
              ? 'none'
              : dayCard.hover
          }
          disregardedBehavior={disregardedBehavior}
          interactionHotZone={''}
          handlerClick={(day, e) => {
            if (!isBlackoutDate && !isEmptyCalendar) {
              handlerDayClick(day, e);
            }
          }}
          enabledCallToAction={false}
          selected={
            (state.start &&
              dayjs(day).isSame(state.start, 'day') &&
              !isOutside &&
              state.selectedComponent === 'start') ||
            (state.end &&
              dayjs(day).isSame(state.end, 'day') &&
              !isOutside &&
              state.selectedComponent === 'end')
          }
          showAsEmptyDay={
            data.length === 0 ||
            (state.start &&
              !isOutside &&
              dayjs(day).isBefore(state.start, 'day') &&
              state.selectedComponent === 'end')
          }
          focusDate={state.focusDate}
          selectedDate={state.start ?? state.firstDay}
          setFocusDate={setFocusDate}
          updateFocusDate={() => {}}
          accessibleByArrowKeys={true}
          makeClickAction={state.clickAction}
          updateMakeClickAction={(value) => {
            dispatch({ type: 'SET_CLICK_ACTION', payload: value });
          }}
          monthShow={dayjs(props.filtersState.dateRange.value.start).get('month')}
        />
      </Box>
    );

    return usedErrorBoundary ? <ErrorBoundary>{dayRender}</ErrorBoundary> : { dayRender };
  };
  const summarySectionRef = React.useRef();

  const {
    NavComponentInstance,
    numberOfMonths,
    navComponent,
    datePicker,
    usedErrorBoundary,
    initialMonth,
    currentMonth,
    data,
    filtersState
  } = props;
  const fareItemCallToAction = {
    ...props.fareItemAtomSettings.fareItemCallToAction,
    focusReferences: (e) => {
      bookNowControlRef.current = e;
    }
  };
  const fareItemAtomSettingsProps = { ...props.fareItemAtomSettings, fareItemCallToAction };
  const isEmptyCalendar = data === null || data.length === 0;
  const enabledReturnDate = filtersState.flightType.value === ROUND_TRIP && state.start;

  const calendar = (
    <DatePicker
      {...datePicker}
      captionElement={() => null}
      canChangeMonth={false}
      month={currentMonth}
      fromMonth={initialMonth}
      toMonth={toMonth}
      selectedDays={getSelectedDate()}
      renderDay={renderDay}
    />
  );

  const navigation = NavComponentInstance && (
    <NavComponentInstance
      months={numberOfMonths}
      fromMonth={initialMonth}
      {...navComponent}
      onSelect={onSelectNavMonth}
      currentMonth={currentMonth}
      isMobile={!props.isDesktop}
      selectedStartDate={state.start}
      activeTab={state.selectedComponent}
    />
  );

  const handleTabsChange = (index) => {
    if (index === 0) {
      departureDateControlRef?.current?.onClick();
      departureTabRef?.current?.focus();
    } else {
      returnDateControlRef?.current?.onClick();
      returnTabRef?.current?.focus();
    }
  };

  const returnTabRef = useRef(null);
  const departureTabRef = useRef(null);

  const fareCalendarRender = (
    <Box width="100" className="fr-fare-calendar">
      <Tabs keyboardActivation={'manual'} onChange={handleTabsChange}>
        <Row>
          <Col {...props.dateControlColumnSettings}>
            <TabList
              arial-label={translate('cmp-low-fare-calendar-oi-tab-list-aria-label', 'Dates')}
            >
              <Inline sx={{ maxWidth: 'tabListWidth' }}>
                <Tab
                  // variant="buttons.primary"
                  sx={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '0px',
                    textAlign: 'unset'
                  }}
                  ref={departureTabRef}
                  tabindex={state.selectedComponent === 'start' ? 0 : -1}
                  aria-selected={state.selectedComponent === 'start' ? true : false}
                >
                  <DateControl
                    {...props.dateControlSettings}
                    ref={departureDateControlRef}
                    labelKey="departure"
                    date={isEmptyCalendar ? undefined : state.start}
                    showSuggested={false}
                    onClick={(date) => {
                      if (state.start) {
                        let newDate;
                        if (
                          dayjs().get('month') === dayjs(state.start).get('month') &&
                          dayjs().get('year') === dayjs(state.start).get('year')
                        ) {
                          newDate = dayjs().toDate();
                        } else {
                          newDate = dayjs(state.start).set('date', 1).toDate();
                        }
                        if (
                          props.filtersState.histogramType !== '' ||
                          (!dayjs(props.filtersState.dateRange.value.start).isSame(
                            state.firstDay,
                            'day'
                          ) &&
                            filtersState.flightType.value !== ONE_WAY &&
                            state.selectedComponent !== 'start')
                        ) {
                          props.globalDispatch({
                            type: 'DATE_RANGE_HISTOGRAM_CHANGE',
                            payload: {
                              dateRange: {
                                start: newDate,
                                end: dayjs(state.start).endOf('month').toDate(),
                                selected: !state.start
                                  ? dayjs(state.start).startOf('month').toDate()
                                  : dayjs(state.start).toDate()
                              },
                              histogramType: ''
                            }
                          });
                          dispatch({ type: 'SET_FOCUS_DATE', payload: state.start });
                        }
                        dispatch({ type: 'SET_SELECTED_COMPONENT', payload: 'start' });
                        dispatch({ type: 'SET_FIRST_DAY', payload: newDate });
                        props.setActiveTab('start');
                        dispatch({ type: 'SET_CLICK_ACTION', payload: true });
                        departureTabRef?.current?.focus();
                      }
                    }}
                    isMobile={!props.isDesktop}
                    isSelected={state.selectedComponent === 'start'}
                  />
                </Tab>
                {props.filtersState.flightType.value === ROUND_TRIP && (
                  <Tab
                    sx={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      border: 'none',
                      padding: '0px',
                      textAlign: 'unset'
                    }}
                    ref={returnTabRef}
                    tabindex={state.selectedComponent === 'end' ? 0 : -1}
                    disabled={state.start === undefined || state.start === null}
                    aria-selected={state.selectedComponent === 'end' ? true : false}
                  >
                    <DateControl
                      {...props.dateControlSettings}
                      ref={returnDateControlRef}
                      labelKey="return"
                      date={isEmptyCalendar ? undefined : state.end}
                      showSuggested={false}
                      onClick={(date) => {
                        if (
                          props.filtersState.histogramType === '' &&
                          filtersState.flightType.value !== ONE_WAY &&
                          state.selectedComponent !== 'end'
                        ) {
                          dispatch({ type: 'SET_SUGGEST_FARE', payload: undefined });
                          if (state.start) {
                            props.globalDispatch({
                              type: 'DATE_RANGE_HISTOGRAM_CHANGE',
                              payload: {
                                dateRange: {
                                  start: state.end
                                    ? dayjs(state.end).get('month') !==
                                      dayjs(state.start).get('month')
                                      ? dayjs(state.end).startOf('month').toDate()
                                      : state.start
                                    : state.start,
                                  end: state.end
                                    ? dayjs(state.end).endOf('month').toDate()
                                    : dayjs(state.start).endOf('month').toDate(),
                                  selected: state.start
                                },
                                histogramType: 'INBOUND'
                              }
                            });
                            dispatch({ type: 'SET_SELECTED_COMPONENT', payload: 'end' });
                            dispatch({ type: 'SET_FIRST_DAY', payload: state.start });
                            props.setActiveTab('end');
                            if (state.end === undefined) {
                              dispatch({ type: 'SET_FOCUS_DATE', payload: state.start });
                            } else {
                              dispatch({ type: 'SET_FOCUS_DATE', payload: state.end });
                            }
                          }
                          dispatch({ type: 'SET_CLICK_ACTION', payload: true });
                          departureTabRef?.current?.focus();
                        }
                      }}
                      disabled={!enabledReturnDate || isEmptyCalendar}
                      isMobile={!props.isDesktop}
                      isSelected={state.selectedComponent === 'end' && enabledReturnDate}
                    />
                  </Tab>
                )}
              </Inline>
              <Row>
                <Col></Col>
                <Col>
                  <SuggestedDate
                    {...props.dateControlSettings}
                    disabled={!enabledReturnDate || isEmptyCalendar}
                    isMobile={!props.isDesktop}
                    onClickSuggestedDate={(e) => {
                      let newfare = props.data.filter(
                        (item) =>
                          dayjs(state.suggestFare.day).format('MM/DD/YYYY') ===
                          dayjs(item.day).format('MM/DD/YYYY')
                      )[0];
                      if (newfare !== undefined && newfare.fareItem.dataType === EMPTY_FROM_API) {
                        newfare.fareItem.returnDate = dayjs(state.suggestFare.day).format(
                          'YYYY-MM-DD'
                        );
                        newfare.fareItem.departureDate = dayjs(state.start).format('YYYY-MM-DD');
                      } else if (newfare === undefined) {
                        newfare = {
                          day: state.suggestFare.day,
                          isLowest: false,
                          fareItem: {
                            dataType: EMPTY_FROM_API,
                            departureDate: dayjs(state.start).format('YYYY-MM-DD'),
                            isBlackoutDay: false,
                            returnDate: dayjs(state.suggestFare.day).format('YYYY-MM-DD')
                          }
                        };
                      }
                      dispatch({ type: 'SET_END_DAY', payload: state.suggestFare.day });
                      dispatch({ type: 'SET_FARE', payload: newfare });
                      props.globalDispatch({
                        type: 'DATE_RANGE_HISTOGRAM_SET_SUGGESTED_DATE',
                        payload: {
                          dateRange: {
                            ...props.filtersState.dateRange,
                            end: state.suggestFare.day,
                            start: state.start
                          },
                          histogramType: ''
                        }
                      });
                      props.setIsStartAndReturnDateSelected(state.start !== undefined);
                      dispatch({ type: 'SET_ENTER_ACTION', payload: undefined });
                      returnTabRef?.current?.focus();
                    }}
                    showSuggested={
                      isOriginSelected &&
                      isDestinationSelected &&
                      !isEmptyCalendar &&
                      state.selectedComponent === 'end' &&
                      state.end === undefined &&
                      state.suggestFare !== undefined
                    }
                    suggestedData={
                      state.suggestFare
                        ? {
                            date: state.suggestFare ? state.suggestFare.day : null,
                            currencyCode: state.suggestFare
                              ? state.suggestFare.fareItem.currencyCode
                              : null,
                            price: state.suggestFare ? state.suggestFare.fareItem.totalPrice : null
                          }
                        : undefined
                    }
                    endDate={state.end}
                  />
                </Col>
              </Row>
            </TabList>
          </Col>
          {props.isDesktop && (
            <Col ms={3} xs={3} lg={3} {...props.fareItemAtomColumnSettings}>
              <FareItemMediumSize
                {...fareItemAtomSettingsProps}
                {...itemSettings}
                {...settingsFareItemMediumSize(state.fare, isEmptyCalendar)}
                enabledDataForSR={true}
                ariaHidden={
                  filtersState.flightType.value === ONE_WAY
                    ? state.start !== null && state.start !== undefined
                      ? false
                      : true
                    : state.start !== null &&
                      state.start !== undefined &&
                      state.end !== undefined &&
                      state.end !== null
                    ? false
                    : true
                }
              />
            </Col>
          )}
        </Row>

        {/* </Row> */}
        {!props.isDesktop && (
          <>
            <Row>
              <Col>
                <FareItemMediumSize
                  {...props.fareItemAtomSettings}
                  {...itemSettings}
                  {...settingsFareItemMediumSize(state.fare, isEmptyCalendar)}
                  onlyFareFooterItem={true}
                  focusReferences={(e) => {
                    summarySectionRef.current = e;
                  }}
                />
              </Col>
            </Row>
          </>
        )}
        <TabPanels>
          <TabPanel
            tabIndex={-1}
            hidden={
              state.selectedComponent === 'start' || state.selectedComponent === undefined
                ? false
                : true
            }
          >
            {navigation}
            {calendar}
          </TabPanel>
          <TabPanel tabIndex={-1} hidden={state.selectedComponent === 'end' ? false : true}>
            {navigation}
            {calendar}
          </TabPanel>
        </TabPanels>
      </Tabs>
      {props.showLegend && (
        <Row {...props.LegendRowSettings}>
          <Col md="12" {...props.legendContentSettings}>
            <LegendCalendar isMobile={!props.isDesktop} {...props.legend} />
          </Col>
        </Row>
      )}
    </Box>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        {usedErrorBoundary ? (
          <ErrorBoundary>{fareCalendarRender}</ErrorBoundary>
        ) : (
          { fareCalendarRender }
        )}
      </ThemeProvider>
    </EmotionCache>
  );
}

FareCalendar.propTypes = {
  /**
   * Component to show in navbar
   */
  NavComponentInstance: PropTypes.func.isRequired,
  /**
   * Component to show in cad
   */
  DayComponentInstance: PropTypes.func.isRequired,
  /**
   * Months to show
   */
  numberOfMonths: PropTypes.number,
  /**
   * NavComponentInstance styles props
   */
  navComponent: PropTypes.objectOf(PropTypes.any),
  /**
   * dayCard component props
   */
  dayCard: PropTypes.objectOf(PropTypes.any),
  /**
   * Array of fares to show in calendar
   */
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Fires when month selected is changed
   */
  onSelectMonth: PropTypes.func,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * DatePicker settings
   */
  datePicker: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if is desktop
   */
  isDesktop: PropTypes.bool,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Define the initial month of the calendar
   */
  initialMonth: PropTypes.instanceOf(Date),
  currentMonth: PropTypes.instanceOf(Date),
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date'])
};
FareCalendar.defaultProps = {
  numberOfMonths: 3,
  navComponent: {},
  dayCard: {},
  data: [],
  onSelectMonth: () => {},
  loading: false,
  usedErrorBoundary: true, // By default all components protect themselves
  datePicker: {},
  isDesktop: true,
  initialMonth: dayjs().toDate(),
  currentMonth: dayjs().toDate(),
  disregardedBehavior: 'past-date'
};

FareCalendar.contextType = Context;

FareCalendar.componentName = 'FareCalendar';

// export default FareCalendar;
export default memo(
  FareCalendar,
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.loading === nextProps.loading &&
    JSON.stringify(prevProps.filtersState) === JSON.stringify(nextProps.filtersState)
);
